import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, LinearProgress, Stack, Typography } from '@mui/material';
import { SDialog } from '@modules/ui-components/dialogs/SDialog';
import { ExperimentsApiClient, ExperimentsApiError } from '../api/experimentsClient';
import { EXPERIMENTS_TEST_IDS } from '../experimentsTestIds';
import type { ResearchCycleProductPreflight, ResearchCycleProductState } from '../researchCycle';

interface ResearchCycleLauncherProps {
  projectId: string;
  reportId: string;
}

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

export const ResearchCycleLauncher: React.FC<ResearchCycleLauncherProps> = ({ projectId, reportId }) => {
  const client = useMemo(() => new ExperimentsApiClient(), []);
  const idempotencyKey = useMemo(
    () => `research-cycle-${reportId}-v1`.slice(0, 190),
    [reportId],
  );
  const [open, setOpen] = useState(false);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [preflight, setPreflight] = useState<ResearchCycleProductPreflight | null>(null);
  const [cycle, setCycle] = useState<ResearchCycleProductState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mutating, setMutating] = useState(false);

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoadState('loading');
    setError(null);
    try {
      const nextPreflight = await client.preflightResearchCycle(
        projectId,
        reportId,
        idempotencyKey,
        signal,
      );
      setPreflight(nextPreflight);
      setCycle(nextPreflight.current);
      setLoadState('ready');
    } catch (loadError) {
      if (signal?.aborted) return;
      setError(productError(loadError));
      setLoadState('error');
    }
  }, [client, idempotencyKey, projectId, reportId]);

  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, open]);

  useEffect(() => {
    if (!open || !cycle || !isRunning(cycle.status)) return undefined;
    const timer = window.setInterval(() => {
      void client.getResearchCycle(projectId, cycle.runId)
        .then(setCycle)
        .catch((pollError) => setError(productError(pollError)));
    }, 5_000);
    return () => window.clearInterval(timer);
  }, [client, cycle, open, projectId]);

  const start = useCallback(async () => {
    setMutating(true);
    setError(null);
    try {
      setCycle(await client.startResearchCycle(projectId, reportId, idempotencyKey));
    } catch (startError) {
      setError(productError(startError));
    } finally {
      setMutating(false);
    }
  }, [client, idempotencyKey, projectId, reportId]);

  const retry = useCallback(async () => {
    if (!cycle) return;
    setMutating(true);
    setError(null);
    try {
      setCycle(await client.retryResearchCycle(
        projectId,
        cycle.runId,
        cycle.revision,
        `${cycle.runId}-product-retry-${cycle.revision}`,
      ));
    } catch (retryError) {
      setError(productError(retryError));
    } finally {
      setMutating(false);
    }
  }, [client, cycle, projectId]);

  const primaryAction = cycle && (cycle.status === 'result_ready' || cycle.status === 'reused')
    ? undefined
    : cycle?.canRetry
    ? {
        label: 'Retry failed stage',
        onClick: retry,
        loading: mutating,
        'data-testid': EXPERIMENTS_TEST_IDS.researchCycleRetry(reportId),
      }
    : !cycle
      ? {
          label: 'Start Research Cycle',
          onClick: start,
          loading: mutating,
          disabled: loadState !== 'ready' || !preflight?.preflight.createAllowed,
          'data-testid': EXPERIMENTS_TEST_IDS.researchCycleStart(reportId),
        }
      : {
          label: 'Running…',
          onClick: () => undefined,
          disabled: true,
          'data-testid': EXPERIMENTS_TEST_IDS.researchCycleDone(reportId),
        };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={() => setOpen(true)}
        data-testid={EXPERIMENTS_TEST_IDS.researchCycleOpen(reportId)}
      >
        Research cycle
      </Button>
      <SDialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        title="Research Cycle"
        primaryAction={primaryAction}
        data-testid={EXPERIMENTS_TEST_IDS.researchCycleDialog}
      >
        <Stack spacing={2} sx={{ minWidth: 420, px: 3 }}>
          {loadState === 'loading' && (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }} data-testid={EXPERIMENTS_TEST_IDS.researchCycleLoading}>
              <CircularProgress size={30} />
              <Typography variant="body2" color="text.secondary">Checking data, privacy, and billing readiness…</Typography>
            </Stack>
          )}

          {error && (
            <Alert
              severity="error"
              data-testid={EXPERIMENTS_TEST_IDS.researchCycleError}
              action={loadState === 'error'
                ? <Button size="small" onClick={() => void load()}>Retry</Button>
                : undefined}
            >
              {error}
            </Alert>
          )}

          {preflight && loadState === 'ready' && (
            <Box data-testid={EXPERIMENTS_TEST_IDS.researchCyclePreflight(reportId)}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip size="small" variant="outlined" color="success" label="Report · deterministic · $0 AI" />
                <Chip
                  size="small"
                  variant="outlined"
                  color={preflight.preflight.panel.allowed ? 'primary' : 'warning'}
                  label={`Expert panel · AI metered · est. $${preflight.panel.estimatedCostUsd.toFixed(2)}`}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  color={preflight.preflight.privacy.status === 'accepted' ? 'success' : 'warning'}
                  label={`Privacy · ${preflight.preflight.privacy.status}`}
                />
              </Stack>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {preflight.report.window.from.slice(0, 10)} → {preflight.report.window.to.slice(0, 10)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                The report is reused without AI spend. The expert panel is charged once through the project AI usage meter.
              </Typography>
              {!preflight.preflight.createAllowed && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Research Cycle is not ready: {preflight.preflight.blockers.join(', ') || 'required checks are unavailable'}.
                </Alert>
              )}
            </Box>
          )}

          {cycle && (
            <Box data-testid={EXPERIMENTS_TEST_IDS.researchCycleStatus(reportId)}>
              {isRunning(cycle.status) && <LinearProgress sx={{ mb: 1.5 }} data-testid={EXPERIMENTS_TEST_IDS.researchCycleProgress(reportId)} />}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {['Report', 'Expert panel', 'Result'].map((label, index) => (
                  <Chip
                    key={label}
                    size="small"
                    variant="outlined"
                    color={stageColor(cycle.status, index)}
                    label={`${label} · ${stageLabel(cycle.status, index)}`}
                  />
                ))}
              </Stack>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {statusCopy(cycle)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Billing: {cycle.billingStatus === 'settled' ? 'settled through the AI usage meter' : cycle.billingStatus}.
              </Typography>
              {(cycle.status === 'result_ready' || cycle.status === 'reused') && (
                <Alert severity="success" sx={{ mt: 1 }} data-testid={EXPERIMENTS_TEST_IDS.researchCycleResult(reportId)}>
                  Research Cycle result is ready for strategy reuse.
                </Alert>
              )}
            </Box>
          )}
        </Stack>
      </SDialog>
    </>
  );
};

function isRunning(status: ResearchCycleProductState['status']): boolean {
  return ['requested', 'report_running', 'report_ready', 'panel_running', 'panel_ready'].includes(status);
}

function stageIndex(status: ResearchCycleProductState['status']): number {
  if (status === 'requested' || status === 'report_running') return 0;
  if (status === 'report_ready' || status === 'panel_running' || status === 'failed') return 1;
  return 2;
}

function stageLabel(status: ResearchCycleProductState['status'], index: number): string {
  const current = stageIndex(status);
  if (index < current) return 'done';
  if (index > current) return 'waiting';
  if (status === 'failed') return 'failed';
  if (status === 'result_ready' || status === 'reused') return 'ready';
  return 'running';
}

function stageColor(
  status: ResearchCycleProductState['status'],
  index: number,
): 'default' | 'primary' | 'success' | 'error' {
  const label = stageLabel(status, index);
  if (label === 'done' || label === 'ready') return 'success';
  if (label === 'failed') return 'error';
  if (label === 'running') return 'primary';
  return 'default';
}

function statusCopy(cycle: ResearchCycleProductState): string {
  if (cycle.status === 'failed') {
    return `The ${cycle.failure?.stage ?? 'current'} stage failed (${cycle.failure?.code ?? 'TASK_FAILED'}). No automatic retry was started.`;
  }
  if (cycle.status === 'result_ready' || cycle.status === 'reused') return 'Report, expert panel, and result are complete.';
  if (cycle.status === 'panel_running') return 'The deterministic report is ready. The metered expert panel is running.';
  return 'Research Cycle is progressing through its durable stages.';
}

function productError(error: unknown): string {
  if (error instanceof ExperimentsApiError) {
    if (error.status === 403) return 'Research Cycle is not available for this account.';
    if (error.status === 402) return 'Research Cycle requires an eligible project AI billing plan.';
    if (error.code === 'capability_not_activated') return 'Research Cycle is not activated for this project.';
    if (error.status === 409) return 'Research Cycle cannot start until its readiness checks pass.';
  }
  return 'Research Cycle is temporarily unavailable. Retry without changing the report.';
}
